// REACT
import { Link } from "react-router-dom";
import { useEffect, useReducer, useState } from "react";

// MATERIAL UI
import { Grid, Stack, Typography, Divider, useTheme, Box, Button, Alert } from "@mui/material";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransactionDisclosure, {
  type DisclosureMetadata,
} from "src/utils/hooks/useTransactionDisclosure";

// STYLES
import { TargetOrderStyles } from "../styles/TargetOrder.style";

// COMPONENTS
import TargetOrderSuccessScreen from "./TargetOrderSuccessScreen";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";

// REDUX
import { useAppDispatch } from "src/store/store";
import { useTargetState, toggleSummeryView } from "src/store/features/TargetOrders.slice";
import {
  CreateTargetOrderCtApiArg,
  GetTargetOrderSummaryApiArg,
} from "src/store/features/api/generated";
import api from "src/store/features/api";

// UTILS
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { dynamicUnitFormatter } from "src/utils/helpers/PriceAndUnitsHelpers";
import TargetOrderProjectionsToolTip from "./tooltips/TargetOrderProjectionsToolTip";

// ICONS
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

const TargetOrderSummery = () => {
  //------------------------STYLES-------------------
  const theme = useTheme();
  const classes = TargetOrderStyles();

  const dispatch = useAppDispatch();
  const [getTargetSummery, { data, error: summeryError }] = api.useGetTargetOrderSummaryMutation();
  const { data: user } = api.useGetUserDatav2Query();
  //todo: throw errors
  const [createTargetOrderCt, { isLoading: creatingCt, isSuccess: ctCreated, isError }] =
    api.useCreateTargetOrderCtMutation();
  const { selectedToken, selectedCurrency, orderType, tradeAmount, targetPrice } = useTargetState();
  //------------------------HOOKS-------------------
  const [ctSuccess, toggleCtSuccess] = useReducer((isSuccess) => !isSuccess, false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isBypassLock, setIsBypassLock] = useState(false);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // ================= Transaction-specific disclosure =================
  // Don't auto-open disclosure - we'll control it manually
  const {
    transactionDisclosure,
    requiresDisclosure,
    isLoading: isDisclosureLoading,
    disclosurePayload,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId: string, metadata?: DisclosureMetadata) => {
    if (pendingSubmit) {
      processTargetSubmit(metadata);
    }
    setPendingSubmit(false);
    setShowDisclosureModal(false);
  }, "target_order");

  // Override the modal open state to control it manually
  const disclosureModalProps = {
    ...modalProps,
    open: showDisclosureModal && !!transactionDisclosure,
    onClose: () => {
      // If disclosure is closed without accepting, just close the disclosure modal
      setShowDisclosureModal(false);
      setPendingSubmit(false);
      handleDisclosureClose();
    },
  };
  //------------------------CONSTS-------------------
  let customerDate = new Date().toLocaleString();
  const HEADER = "Order Details";
  const INFO_BOX_DESC =
    "By clicking “Place Target”, you agree that your order will be entered into our system, and the assets required to fulfil your order will be reserved until executed or cancelled.";
  const INFO_BOX_DESC_TWO =
    "To cancel your Target Order, you may do so via the Portal or by emailing your Broker. Cancellation requests made when the Market Price is within 5% of your Target Price may not be honoured.";
  const MFA_MESSAGE_PARTA = `Please enable two-factor authentication `;
  const MFA_MESSAGE_PARTB = "to place an order.";
  const LOCK_ACCOUNT_MESSAGE = `Hmmm something went wrong placing your order. Please contact your Broker to enter this trade.`;
  const TOOLTIP_EXPECTED =
    "The amount of money you will receive if your Target Order executes at the price you chose." as const;
  const TOOLTIP_BROKERFEE =
    "Your trade has one all-inclusive broker fee covering costs associated with slippage, foreign exchange, and labour involved. A further breakdown will be provided in your invoice" as const;
  const TOOLTIP_REMAINING =
    "This is how much of the token you have left after you place this order. You can also check this on your Wallet page.";
  //------------------------Effects-------------------

  useEffect(() => {
    if (ctCreated) {
      toggleCtSuccess();
    }
  }, [ctCreated]);

  useEffect(() => {
    if (isError) {
      console.error(`Error occurred Target Orders:`);
    }
  }, [isError]);

  useEffect(() => {
    if (summeryError) {
      console.error(`Error occurred Target Orders Summery:`);
    }
  }, [summeryError]);

  useEffect(() => {
    if (user) {
      if (!user.is2FAEnabled) {
        setMfaEnabled(true);
      } else if (user.isLocked && !user.isBypassLock) {
        setIsLocked(true);
        setIsBypassLock(false);
      } else if (user.isLocked && user.isBypassLock) {
        setIsLocked(true);
        setIsBypassLock(true);
      }
    }
  }, [ctCreated]);

  useEffect(() => {
    if (!selectedToken?.code || !selectedCurrency?.code || !targetPrice || !tradeAmount) return;

    if (orderType[0] === "Buy") {
      const buyReqBody: GetTargetOrderSummaryApiArg = {
        targetOrderSummaryDto: {
          buy: {
            targetPrice: targetPrice,
            units: tradeAmount,
            code: selectedToken.code,
          },
          sell: { code: selectedCurrency.code },
        },
      };
      getTargetSummery(buyReqBody);
    } else {
      const sellReqBody: GetTargetOrderSummaryApiArg = {
        targetOrderSummaryDto: {
          buy: { code: selectedCurrency.code },
          sell: {
            targetPrice: targetPrice,
            units: tradeAmount,
            code: selectedToken.code,
          },
        },
      };
      getTargetSummery(sellReqBody);
    }
  }, []);

  // ----------------------- Handlers -----------------------------

  // Process target order submission (after disclosure is accepted or when no disclosure required)
  const processTargetSubmit = (disclosureMetadata?: DisclosureMetadata) => {
    const timezone =
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
    const disclosureFields = (disclosurePayload || disclosureMetadata) && {
      disclosureRequestDateTime:
        disclosurePayload?.disclosureRequestDateTime ||
        disclosureMetadata?.disclosureRequestDateTime,
      disclosureAcceptanceDateTime:
        disclosurePayload?.disclosureAcceptanceDateTime ||
        disclosureMetadata?.disclosureAcceptanceDateTime,
      timezone: timezone,
    };

    if (orderType[0] === "Buy") {
      trackEvent(`Target_Order_Submitted`, {
        Submit_Target_Buy: selectedToken.code,
      });
      const buyReqBody: CreateTargetOrderCtApiArg = {
        createTargetOrderDto: {
          buy: {
            targetPrice: targetPrice,
            units: tradeAmount,
            code: selectedToken.code,
          },
          sell: { code: selectedCurrency.code },
          datetime: customerDate,
          ...(disclosureFields ?? {}),
        },
      };
      createTargetOrderCt(buyReqBody);
    } else {
      trackEvent(`Target_Order_Submitted`, {
        Submit_Target_Sell: selectedToken.code,
      });
      const sellReqBody: CreateTargetOrderCtApiArg = {
        createTargetOrderDto: {
          buy: { code: selectedCurrency.code },
          sell: {
            targetPrice: targetPrice,
            units: tradeAmount,
            code: selectedToken.code,
          },
          datetime: customerDate,
          ...disclosureFields,
        },
      };
      createTargetOrderCt(sellReqBody);
    }
  };

  const handleTargetSubmit = () => {
    // Don't proceed while the disclosure is still resolving — a null transactionDisclosure
    // (and thus requiresDisclosure === false) means "unknown", not "not required".
    if (isDisclosureLoading) {
      return;
    }

    // Check if disclosure is required (MN/CT and not already accepted today)
    if (requiresDisclosure && !showDisclosureModal) {
      // Store the submission intent and show disclosure modal
      setPendingSubmit(true);
      setShowDisclosureModal(true);
      return;
    }

    // If no disclosure required, proceed directly (no metadata)
    processTargetSubmit(undefined);
  };

  if (creatingCt) return <LoadingSpinner size={60} />;

  if (ctSuccess) return <TargetOrderSuccessScreen />;
  return (
    <Grid container sx={{ padding: "0px 24px 0px 24px" }}>
      <Grid item sx={{ height: "75px", width: "100%" }}>
        <Stack
          sx={{ height: "100%", width: "100%" }}
          direction="row"
          alignItems="center"
          justifyContent="flex-start">
          {data && (
            <ArrowBackIosIcon
              onClick={() => {
                dispatch(toggleSummeryView());
                trackEvent(`Target_Order_Summary_Return_Btn`);
              }}
              sx={{
                cursor: "pointer",
                color: theme.palette.secondary.main,
                marginLeft: "0px",
                width: "15px",
                height: "15px",
              }}
            />
          )}
          <Typography
            sx={{
              flex: 1,
              width: "220px",
              textAlign: "center",
              fontSize: "16px",
              fontWeight: 700,
            }}>
            {HEADER}
          </Typography>
        </Stack>
        <Divider />
      </Grid>
      {!data && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100%"
          width="100%"
          sx={{ height: "600px" }}>
          <LoadingSpinner size={60} />
        </Box>
      )}
      {data && !creatingCt && (
        <Grid item sx={{ width: "100%", paddingTop: "16px" }}>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Pairing</Typography>
            <Typography className={classes.summaryTextTitle}>
              {`${data?.sell.currencyConfig.code}/${data?.buy.currencyConfig.code}`}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Sell</Typography>
            <Typography className={classes.summaryTextTitle}>
              {data?.sell.currencyConfig.code}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Quoted Units</Typography>
            <Typography className={classes.summaryTextTitle}>
              {orderType[0] === "Sell"
                ? dynamicUnitFormatter(data.sell.units, 0)
                : dynamicUnitFormatter(data.buy.units, 0)}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Stack
              direction="row"
              sx={{ alignItems: "center" }}
              className={classes.summaryTextTitle}>
              <Typography
                sx={{
                  position: "relative",
                  fontSize: "14px",
                  fontWeight: 600,
                  paddingRight: "4px",
                }}>
                Remaining Balance
              </Typography>
              <TargetOrderProjectionsToolTip message={TOOLTIP_REMAINING} />
            </Stack>

            <Typography className={classes.summaryTextTitle}>
              {orderType[0] === "Sell"
                ? dynamicUnitFormatter(data.remainingUnits, 0)
                : formatCurrency(data.remainingUnits, data.quoteCurrencyConfig.code)}
              {orderType[0] === "Sell" && data?.sell.currencyConfig.code}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Buy</Typography>
            <Typography className={classes.summaryTextTitle}>
              {data?.buy.currencyConfig.code}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Target Price</Typography>
            <Typography className={classes.summaryTextTitle}>
              {orderType[0] === "Sell"
                ? formatCurrency(data.sell.price, `${data?.quoteCurrencyConfig.code}`)
                : formatCurrency(data.buy.price, `${data?.quoteCurrencyConfig.code}`)}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Market Price</Typography>
            <Typography className={classes.summaryTextTitle}>
              {formatCurrency(data?.knownCurrentPrice || 0, `${data?.quoteCurrencyConfig.code}`)}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Typography className={classes.summaryTextTitle}>Target Distance</Typography>
            <Stack sx={{ minWidth: "50%" }}>
              <Typography className={classes.summaryTextTitle}>
                {parseFloat(Math.abs(data.percentageDifference).toFixed(2))}% from Market
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row">
            <Stack
              direction="row"
              sx={{ alignItems: "center" }}
              className={classes.summaryTextTitle}>
              <Typography
                sx={{
                  position: "relative",
                  fontSize: "14px",
                  fontWeight: 600,
                  paddingRight: "4px",
                }}>
                Expected Output
              </Typography>
              <TargetOrderProjectionsToolTip message={TOOLTIP_EXPECTED} />
            </Stack>

            <Typography className={classes.summaryTextTitle}>
              {formatCurrency(
                orderType[0] === "buy" ? data?.sell.value : data?.buy.value,
                `${data?.quoteCurrencyConfig.code}`,
              )}
            </Typography>
          </Stack>
          <Stack direction="row">
            <Stack
              direction="row"
              sx={{ alignItems: "center" }}
              className={classes.summaryTextTitle}>
              <Typography
                sx={{
                  position: "relative",
                  fontSize: "14px",
                  fontWeight: 600,
                  paddingRight: "4px",
                }}>
                Broker Fee
              </Typography>
              <TargetOrderProjectionsToolTip message={TOOLTIP_BROKERFEE} />
            </Stack>
            <Typography className={classes.summaryTextTitle}>{data?.fee.amount}%</Typography>
          </Stack>
          <Divider />
          <Grid item>
            <Stack direction="row" sx={{ paddingTop: "24px" }}>
              <Typography className={classes.summaryTextTitle}>Total</Typography>
              <Typography className={classes.summaryTextTitle}>
                {formatCurrency(data?.totalValue, `${data?.quoteCurrencyConfig.code}`)}
              </Typography>
            </Stack>
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#f8f9f9",
                padding: "8px",
                borderRadius: "10px",
                color: "#7b858b",
              }}>
              <Typography sx={{ fontSize: "12px", lineHeight: "1.17" }}>{INFO_BOX_DESC}</Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  paddingTop: "10px",
                  lineHeight: "1.17",
                }}>
                {INFO_BOX_DESC_TWO}
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            {mfaEnabled && (
              <Alert sx={{ marginTop: "10px" }} severity="error">
                {MFA_MESSAGE_PARTA}
                <Link to="/settings/security">here </Link>
                {MFA_MESSAGE_PARTB}
              </Alert>
            )}

            {!mfaEnabled && isLocked && !isBypassLock && (
              <Alert sx={{ marginTop: "10px" }} severity="error">
                {LOCK_ACCOUNT_MESSAGE}
              </Alert>
            )}
            {isError && (
              <Alert sx={{ marginTop: "10px" }} severity="error">
                Error. Please contact support or try again
              </Alert>
            )}
            {!data.valid && (
              <Alert sx={{ marginTop: "10px" }} severity="error">
                {data.error}
              </Alert>
            )}
            {!isError && !mfaEnabled && data.valid && (!isLocked || (isLocked && isBypassLock)) && (
              <Button
                disableRipple
                id="target-place-order-button"
                fullWidth
                disabled={mfaEnabled}
                onClick={() => handleTargetSubmit()}
                sx={{
                  marginTop: mfaEnabled ? "10px" : "40px",
                  width: "100%",
                  backgroundColor: theme.palette.secondary.main,
                  color: "#fff",
                  textTransform: "capitalize",
                  "&:hover": { backgroundColor: "#fedbdb" },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(173, 192, 203, 0.3)",
                    color: "#bdc2c5",
                  },
                }}>
                Place Target
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {/* TRANSACTION-SPECIFIC DISCLOSURE MODAL */}
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </Grid>
  );
};

export default TargetOrderSummery;
